import { IdentityEditor } from '@/components/brands/IdentityEditor';
import { legacyBrandChanges } from '@/components/brands/identity-editor-state';
import { BrandAdGallery } from '@/components/brands/BrandAdGallery';
import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { triggerCreditsGate } from '@/lib/credits-gate';
import type { Brand, Product, BrandIntelligence, BrandIntelligenceAd } from '@shared/schema';
import { useToast } from '@/hooks/use-toast';
import {
  Plus,
  ArrowLeft,
  Loader2,
  Tag,
  Search,
  Brain,
  RefreshCw,
  Target,
  Package,
  Lightbulb,
  Trash2,
  X,
  Upload,
  Eye,
  MessageSquare,
  TrendingUp,
  BarChart3,
  Zap,
  Link2,
  ChevronRight,
  Users,
  AlertCircle,
  CheckCircle2,
  Sparkles,
  Palette,
} from 'lucide-react';
import { CompetitorAnalysisContent } from '@/pages/competitor-analysis';
import { useCanvasStore } from './canvas-store';
import { AdAnglesPanel } from '@/components/brands/AdAnglesPanel';
// v1.95 — Brand Identity tab moved out of Heartbeat to live on each brand.
import { BrandIdentityTab } from '@/pages/heartbeat/brand-identity';
import { ProductIdentityTab } from '@/pages/heartbeat/product-identity';

type IntelligenceData = BrandIntelligence & { ads: BrandIntelligenceAd[] };

function SkeletonCard() {
  return (
    <div
      className="rounded-2xl animate-pulse"
      style={{
        background: '#F0EDE8',
        height: 180,
        border: '1px solid #E8E4DC',
      }}
    />
  );
}

function SkeletonHeaderBar() {
  return (
    <div className="flex items-center gap-4 animate-pulse">
      <div style={{ width: 56, height: 56, borderRadius: 14, background: '#E8E4DC', flexShrink: 0 }} />
      <div className="flex-1 space-y-2">
        <div style={{ height: 20, width: 160, background: '#E8E4DC', borderRadius: 6 }} />
        <div style={{ height: 14, width: 100, background: '#F0EDE8', borderRadius: 6 }} />
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status?: string | null }) {
  const colors: Record<string, { bg: string; text: string }> = {
    Active: { bg: '#D1FAE5', text: '#065F46' },
    Inactive: { bg: '#FEE2E2', text: '#991B1B' },
    Draft: { bg: '#FEF3C7', text: '#92400E' },
  };
  const s = status || 'Active';
  const c = colors[s] || { bg: '#E8E4DC', text: '#3D3A36' };
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 20,
        background: c.bg,
        color: c.text,
        letterSpacing: '0.02em',
      }}
    >
      {s}
    </span>
  );
}

function PaletteSwatches({ colors }: { colors?: string[] | null }) {
  if (!colors || colors.length === 0) return null;
  return (
    <div className="flex gap-1">
      {colors.slice(0, 5).map((c, i) => (
        <div
          key={i}
          title={c}
          style={{
            width: 16,
            height: 16,
            borderRadius: 4,
            background: c,
            border: '1px solid rgba(0,0,0,0.08)',
            flexShrink: 0,
          }}
        />
      ))}
    </div>
  );
}

function ModalBackdrop({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  // Render to document.body so the fixed overlay is always viewport-relative and
  // sits above the canvas/embedded-view UI — never trapped inside an ancestor's
  // stacking context or clipped by an overflow container.
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg mx-4 rounded-2xl overflow-hidden"
        style={{ background: '#FAFAF8', border: '1px solid #E8E4DC', boxShadow: '0 24px 60px rgba(0,0,0,0.18)' }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

function ModalHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div
      className="flex items-center justify-between"
      style={{ padding: '18px 24px 14px', borderBottom: '1px solid #E8E4DC' }}
    >
      <span style={{ fontSize: 16, fontWeight: 700, color: '#1A1A1A' }}>{title}</span>
      <button
        onClick={onClose}
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: 'transparent',
          color: '#8C8578',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          border: 'none',
        }}
        onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#EDEDEA')}
        onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
      >
        <X style={{ width: 15, height: 15 }} />
      </button>
    </div>
  );
}

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label style={{ fontSize: 12, fontWeight: 500, color: '#6B6760', display: 'block', marginBottom: 5 }}>
      {children}
      {required && <span style={{ color: '#DC2626', marginLeft: 2 }}>*</span>}
    </label>
  );
}

function InputField({
  value,
  onChange,
  placeholder,
  type = 'text',
  testId,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  testId?: string;
}) {
  return (
    <input
      data-testid={testId}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%',
        padding: '8px 12px',
        borderRadius: 10,
        border: '1px solid #E0DDD8',
        background: '#F7F6F3',
        fontSize: 13.5,
        color: '#1A1A1A',
        outline: 'none',
        boxSizing: 'border-box',
      }}
      onFocus={(e) => (e.currentTarget.style.borderColor = '#B5B0A8')}
      onBlur={(e) => (e.currentTarget.style.borderColor = '#E0DDD8')}
    />
  );
}

function PrimaryButton({
  onClick,
  disabled,
  loading,
  children,
  testId,
}: {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        padding: '9px 20px',
        borderRadius: 10,
        background: disabled || loading ? '#D0CECA' : '#1A1A1A',
        color: '#FFFFFF',
        fontSize: 13.5,
        fontWeight: 600,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {loading && <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />}
      {children}
    </button>
  );
}

function GhostButton({
  onClick,
  children,
  testId,
}: {
  onClick: () => void;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      style={{
        padding: '9px 16px',
        borderRadius: 10,
        background: 'transparent',
        color: '#6B6760',
        fontSize: 13.5,
        fontWeight: 500,
        cursor: 'pointer',
        border: '1px solid #E0DDD8',
      }}
      onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F0EDE8')}
      onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
    >
      {children}
    </button>
  );
}

function DangerButton({
  onClick,
  disabled,
  loading,
  children,
  testId,
}: {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        padding: '9px 20px',
        borderRadius: 10,
        background: disabled || loading ? '#FEE2E2' : '#DC2626',
        color: '#FFFFFF',
        fontSize: 13.5,
        fontWeight: 600,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {loading && <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />}
      {children}
    </button>
  );
}

function AnalyzeBrandModal({ onClose, onSuccess, initialUrl }: { onClose: () => void; onSuccess: () => void; initialUrl?: string }) {
  const [url, setUrl] = useState(initialUrl ?? '');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [urlError, setUrlError] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleSubmit = async () => {
    if (!url) { setUrlError('Website URL is required.'); return; }
    setUrlError('');
    setError('');
    // v7.10 — plan brand cap, checked BEFORE the paid scrape so an over-cap
    // user doesn't burn analysis credits on a brand they can't save. The
    // server enforces the same cap on POST /api/brands (this is courtesy).
    try {
      const [meRes, brandsRes] = await Promise.all([
        fetch('/api/auth/me', { credentials: 'include' }),
        fetch('/api/brands', { credentials: 'include' }),
      ]);
      if (meRes.ok && brandsRes.ok) {
        const me = await meRes.json();
        const existing = await brandsRes.json();
        const max = me?.entitlements?.maxBrands;
        if (typeof max === 'number' && Array.isArray(existing) && existing.length >= max) {
          setError(`You've reached the ${me?.entitlements?.planName ?? 'current'} plan's limit of ${max} brand${max === 1 ? '' : 's'}. Upgrade for more.`);
          return;
        }
      }
    } catch { /* fail open — the server cap still holds */ }
    setIsLoading(true);
    try {
      const res = await fetch('/api/brand/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, name: name || undefined, category: category || undefined }),
      });
      const result = await res.json();
      if (res.status === 402 && result.code === 'CREDITS_EXHAUSTED') {
        triggerCreditsGate(Number(result.balance ?? 0), Number(result.needed ?? 0));
        setError('Not enough credits to analyze this brand.');
        return;
      }
      if (!res.ok || !result.success) {
        setError(result.message || 'Analysis failed. Please try again.');
        return;
      }
      const d = result.data;
      // What the user typed always wins over the analyzer's guess — a parked
      // or malformed page can return an unrelated brand_name (e.g. "Atom").
      const brandPayload = {
        name: name || d.brand_name || '',
        industry: category || d.industry_vertical || '',
        website: d.website || url || '',
        description: d.product_service_description || '',
        targetAudience: d.target_audience || '',
        toneOfVoice: d.tone_of_voice || '',
        logo: d.logo_url || null,
        colors: d.brand_colors || [],
        painPoints: d.pain_points || [],
        purchaseTriggers: d.purchase_triggers || [],
        customerObjections: d.customer_objections || [],
        desiredTransformation: d.desired_transformation || null,
        customerAvatarName: d.customer_avatar_name || null,
        awarenessLevel: d.awareness_level || null,
        brandPersonality: d.brand_personality || [],
        contentPillars: d.content_pillars || [],
        voiceWordsUsed: d.voice_words_used || [],
        voiceWordsAvoided: d.voice_words_avoided || [],
        additionalInfo: d.additional_information || '',
        competitorPositioning: d.competitor_positioning || null,
        keyStats: d.key_stats || [],
        heroTestimonials: d.hero_testimonials || [],
        pressMentions: d.press_mentions || [],
        beforeAfterResults: d.before_after_results || [],
        priceAnchoring: d.price_anchoring || null,
        hasSubscription: d.has_subscription || false,
        heroSku: d.hero_sku || null,
        offerPatterns: d.offer_patterns || [],
        photographyStyle: d.photography_style || [],
        brandEnvironments: d.brand_environments || [],
        modelDirection: d.model_direction || null,
        ageRange: d.age_range || null,
        genderSkew: d.gender_skew || null,
        geographyFocus: d.geography_focus || [],
        platformPrimary: d.platform_primary || [],
        audienceInterests: d.audience_interests || [],
        funnelStage: d.funnel_stage || null,
        funnelGoal: d.funnel_goal || null,
        headlineFont: d.fonts?.headline?.family || null,
        headlineFontWeight: d.fonts?.headline?.weight || null,
        bodyFont: d.fonts?.body?.family || null,
        bodyFontWeight: d.fonts?.body?.weight || null,
      };
      const saveRes = await fetch('/api/brands', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(brandPayload),
      });
      const saveResult = await saveRes.json();
      if (!saveRes.ok) {
        setError(saveResult.message || 'Brand analyzed but could not be saved. Please try again.');
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['/api/brands'] });
      // v7.50 — the server made the brand's Projects folder (v7.37 hook);
      // refetch the sidebar so it shows up NOW, not after a page refresh.
      queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', 'rootOnly'] });
      queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', 'all'] });
      queryClient.invalidateQueries({ queryKey: ['/api/brands/onboarding/active'] });
      toast({ title: 'Brand analyzed', description: 'Brand has been created from your website.' });
      onSuccess();
      onClose();
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalBackdrop onClose={onClose}>
      <ModalHeader title="Analyze new brand" onClose={onClose} />
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <FieldLabel required>Website URL</FieldLabel>
          <InputField
            value={url}
            onChange={setUrl}
            placeholder="https://yourbrand.com"
            testId="input-canvas-brand-url"
          />
          {urlError && <p style={{ fontSize: 12, color: '#DC2626', marginTop: 4 }}>{urlError}</p>}
        </div>
        <div>
          <FieldLabel>Brand name (optional)</FieldLabel>
          <InputField value={name} onChange={setName} placeholder="Brand name" testId="input-canvas-brand-name" />
        </div>
        <div>
          <FieldLabel>Category (optional)</FieldLabel>
          <InputField value={category} onChange={setCategory} placeholder="e.g. E-commerce, SaaS" testId="input-canvas-brand-category" />
        </div>
        {error && (
          <p style={{ fontSize: 12, color: '#DC2626', padding: '8px 12px', borderRadius: 8, background: '#FEF2F2' }}>{error}</p>
        )}
        {isLoading && (
          <p style={{ fontSize: 12, color: '#6B6760', padding: '8px 12px', borderRadius: 8, background: '#F7F6F3' }}>
            Analyzing website and generating brand identity... This may take 15–30 seconds.
          </p>
        )}
      </div>
      <div
        style={{ padding: '14px 24px', borderTop: '1px solid #E8E4DC', display: 'flex', justifyContent: 'flex-end', gap: 10 }}
      >
        <GhostButton onClick={onClose}>Cancel</GhostButton>
        <PrimaryButton onClick={handleSubmit} loading={isLoading} disabled={!url} testId="button-canvas-analyze-brand">
          {isLoading ? 'Analyzing…' : 'Analyze brand'}
        </PrimaryButton>
      </div>
    </ModalBackdrop>
  );
}

function AddProductModal({
  brandId,
  product,
  onClose,
  onSuccess,
}: {
  brandId: number;
  product?: Product | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [productUrl, setProductUrl] = useState(product?.productUrl || '');
  const [name, setName] = useState(product?.name || '');
  const [description, setDescription] = useState(product?.description || '');
  const [photos, setPhotos] = useState<string[]>(product?.photos || []);
  // v6.17 — Product State Descriptions: free-text footnotes about the
  // product's physical states ("pouch contains single-serve packets",
  // "mixed drink is a transparent light blue"). The video engine treats
  // them as ground truth when writing scripts and running QA.
  const [stateDescriptions, setStateDescriptions] = useState<string[]>(
    Array.isArray((product as unknown as { stateDescriptions?: string[] })?.stateDescriptions)
      ? ((product as unknown as { stateDescriptions?: string[] }).stateDescriptions as string[])
      : [],
  );
  // v6.48 — product sheet state. Seeded from the product row so a reopened
  // modal shows the existing sheet immediately; the poll only runs while a
  // generation is actually in flight.
  const productRow = product as unknown as { productSheetUrl?: string | null; sizeSpec?: string | null } | null;
  const [sheetUrl, setSheetUrl] = useState<string | null>(productRow?.productSheetUrl ?? null);
  const [sizeSpecValue, setSizeSpecValue] = useState<string>(productRow?.sizeSpec ?? '');
  const [sheetBusy, setSheetBusy] = useState(false);
  const [sheetError, setSheetError] = useState('');
  const [sheetSuggestions, setSheetSuggestions] = useState<string[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // v6.48 — kick off the sheet (202) then poll the status endpoint. The
  // server owns the lock (productSheetAt set + url null = generating), so a
  // closed-and-reopened modal resumes the poll instead of double-charging.
  const pollSheet = useCallback(async (productId: number) => {
    for (let i = 0; i < 90; i++) {
      await new Promise((r) => setTimeout(r, 4000));
      try {
        const res = await fetch(`/api/products/${productId}/product-sheet/status`);
        if (!res.ok) continue;
        const d = await res.json();
        if (d.status === 'generating') continue;
        setSheetBusy(false);
        if (d.error) { setSheetError(d.error); return; }
        if (d.sheetUrl) {
          setSheetUrl(d.sheetUrl);
          if (d.sizeSpec) setSizeSpecValue(d.sizeSpec);
          if (Array.isArray(d.stateDescriptions) && d.stateDescriptions.length > 0) {
            setStateDescriptions(d.stateDescriptions.map(String));
          }
          if (Array.isArray(d.suggestedStates)) setSheetSuggestions(d.suggestedStates.map(String));
          queryClient.invalidateQueries({ queryKey: ['/api/brands', brandId, 'products'] });
        }
        return;
      } catch { /* keep polling */ }
    }
    setSheetBusy(false);
    setSheetError('Timed out waiting for the sheet — reopen this product to check.');
  }, [brandId, queryClient]);

  const handleCreateSheet = useCallback(async () => {
    if (!product?.id || sheetBusy) return;
    setSheetBusy(true);
    setSheetError('');
    setSheetSuggestions([]);
    try {
      const res = await fetch(`/api/products/${product.id}/product-sheet`, { method: 'POST' });
      if (res.status === 402) {
        const err = await res.json().catch(() => ({}));
        triggerCreditsGate(Number(err.balance ?? 0), Number(err.needed ?? 0));
        setSheetBusy(false);
        return;
      }
      // 409 = a run is already in flight (double-click, or reopened modal):
      // fall through to polling rather than starting a second paid run.
      if (!res.ok && res.status !== 409) {
        const err = await res.json().catch(() => ({}));
        setSheetError(err.message || `Could not start (${res.status})`);
        setSheetBusy(false);
        return;
      }
      void pollSheet(product.id);
    } catch {
      setSheetError('Network error starting the sheet.');
      setSheetBusy(false);
    }
  }, [product?.id, sheetBusy, pollSheet]);

  // Resume the spinner if the modal reopens mid-run.
  useEffect(() => {
    if (!product?.id) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/products/${product.id}/product-sheet/status`);
        if (!res.ok || cancelled) return;
        const d = await res.json();
        if (d.status === 'generating') {
          setSheetBusy(true);
          void pollSheet(product.id);
        } else if (d.sheetUrl) {
          setSheetUrl(d.sheetUrl);
          if (d.sizeSpec) setSizeSpecValue(d.sizeSpec);
        }
      } catch { /* the button still works */ }
    })();
    return () => { cancelled = true; };
  }, [product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAnalyze = async () => {
    if (!productUrl) return;
    setIsAnalyzing(true);
    setAnalyzeError('');
    const controller = new AbortController();
    const clientTimeout = setTimeout(() => controller.abort(), 35_000);
    try {
      const res = await fetch('/api/product/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: productUrl }),
        signal: controller.signal,
      });
      const result = await res.json();
      if (res.status === 402 && result.code === 'CREDITS_EXHAUSTED') {
        triggerCreditsGate(Number(result.balance ?? 0), Number(result.needed ?? 0));
        setAnalyzeError('Not enough credits to analyze this product.');
        return;
      }
      if (!res.ok || !result.success) {
        setAnalyzeError(result.message || 'Analysis failed.');
        return;
      }
      const d = result.data;
      setName(d.product_name || '');
      setDescription(d.description || '');
      if (d.photos && d.photos.length > 0) setPhotos(d.photos);
    } catch (err: unknown) {
      const isAbort = err instanceof Error && err.name === 'AbortError';
      setAnalyzeError(isAbort ? 'Timed out — please fill in the fields manually.' : 'Something went wrong.');
    } finally {
      clearTimeout(clientTimeout);
      setIsAnalyzing(false);
    }
  };

  const handleImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/upload', { method: 'POST', body: formData });
      if (!res.ok) throw new Error('Upload failed');
      const { url } = await res.json();
      setPhotos((prev) => [...prev, url]);
    } catch {
      toast({ title: 'Upload failed', description: 'Could not upload image.', variant: 'destructive' });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [toast]);

  const handleSave = async () => {
    if (!name) return;
    setIsSaving(true);
    try {
      const stateList = stateDescriptions.map((s) => s.trim()).filter(Boolean);
      if (product) {
        await apiRequest('PATCH', `/api/products/${product.id}`, { name, description, productUrl, photos, stateDescriptions: stateList, sizeSpec: sizeSpecValue.trim() || null });
      } else {
        await apiRequest('POST', `/api/brands/${brandId}/products`, { name, description, productUrl, photos, stateDescriptions: stateList });
      }
      queryClient.invalidateQueries({ queryKey: ['/api/brands', brandId, 'products'] });
      toast({ title: product ? 'Product updated' : 'Product added', description: `"${name}" has been ${product ? 'updated' : 'added'}.` });
      onSuccess();
      onClose();
    } catch {
      toast({ title: 'Error', description: 'Could not save product.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalBackdrop onClose={onClose}>
      <ModalHeader title={product ? 'Edit product' : 'Add product'} onClose={onClose} />
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '65vh', overflowY: 'auto' }}>
        {!product && (
          <div style={{ background: '#F7F6F3', borderRadius: 12, padding: '12px 14px', border: '1px solid #E8E4DC' }}>
            <FieldLabel>Product URL (auto-fill)</FieldLabel>
            <div style={{ display: 'flex', gap: 8 }}>
              <InputField
                value={productUrl}
                onChange={setProductUrl}
                placeholder="https://store.com/product"
                testId="input-canvas-product-url"
              />
              <button
                onClick={handleAnalyze}
                disabled={!productUrl || isAnalyzing}
                style={{
                  padding: '8px 14px',
                  borderRadius: 10,
                  border: '1px solid #E0DDD8',
                  background: 'white',
                  fontSize: 13,
                  color: '#3D3A36',
                  cursor: productUrl && !isAnalyzing ? 'pointer' : 'not-allowed',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                }}
                data-testid="button-canvas-analyze-product"
              >
                {isAnalyzing ? <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" /> : null}
                Analyze
              </button>
            </div>
            {analyzeError && <p style={{ fontSize: 12, color: '#DC2626', marginTop: 6 }}>{analyzeError}</p>}
            {isAnalyzing && <p style={{ fontSize: 12, color: '#6B6760', marginTop: 6 }}>Analyzing product page…</p>}
          </div>
        )}
        <div>
          <FieldLabel required>Product name</FieldLabel>
          <InputField value={name} onChange={setName} placeholder="Product name" testId="input-canvas-product-name" />
        </div>
        <div>
          <FieldLabel>Description (optional)</FieldLabel>
          <textarea
            data-testid="input-canvas-product-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Product description"
            rows={3}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: 10,
              border: '1px solid #E0DDD8',
              background: '#F7F6F3',
              fontSize: 13.5,
              color: '#1A1A1A',
              outline: 'none',
              resize: 'vertical',
              boxSizing: 'border-box',
              fontFamily: 'inherit',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = '#B5B0A8')}
            onBlur={(e) => (e.currentTarget.style.borderColor = '#E0DDD8')}
          />
        </div>
        <div>
          <FieldLabel>Product State Descriptions (optional)</FieldLabel>
          <p style={{ fontSize: 12, color: '#6B6760', margin: '0 0 8px' }}>
            Footnotes about the product's different states — the AI treats these as ground truth when
            writing video scripts. E.g. "The pouch contains single-serve packets — powder only ever
            pours from a torn packet" or "The mixed drink is a transparent light blue, never opaque."
          </p>
          {stateDescriptions.map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
              <textarea
                data-testid={`input-product-state-${i}`}
                value={s}
                onChange={(e) => setStateDescriptions((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))}
                placeholder="Describe one product state…"
                rows={2}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: 10,
                  border: '1px solid #E0DDD8',
                  background: '#F7F6F3',
                  fontSize: 13,
                  color: '#1A1A1A',
                  outline: 'none',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = '#B5B0A8')}
                onBlur={(e) => (e.currentTarget.style.borderColor = '#E0DDD8')}
              />
              <button
                onClick={() => setStateDescriptions((prev) => prev.filter((_, j) => j !== i))}
                title="Remove"
                data-testid={`button-remove-product-state-${i}`}
                style={{
                  border: '1px solid #E0DDD8',
                  background: 'white',
                  borderRadius: 8,
                  width: 28,
                  height: 28,
                  cursor: 'pointer',
                  color: '#6B6760',
                  fontSize: 15,
                  lineHeight: 1,
                  flexShrink: 0,
                  marginTop: 4,
                }}
              >
                ×
              </button>
            </div>
          ))}
          <button
            onClick={() => setStateDescriptions((prev) => [...prev, ''])}
            data-testid="button-add-product-state"
            style={{
              padding: '7px 12px',
              borderRadius: 10,
              border: '1px dashed #C9C4BC',
              background: 'white',
              fontSize: 12.5,
              color: '#3D3A36',
              cursor: 'pointer',
            }}
          >
            + Add state description
          </button>
        </div>

        {/* v6.48 — PRODUCT SHEET. Edit mode only: the add flow has no product
            id yet (handleSave creates the row), and the sheet generates from
            an existing product photo. */}
        {product?.id != null && (
          <div>
            <FieldLabel>Product Sheet</FieldLabel>
            <p style={{ fontSize: 12, color: '#6B6760', margin: '0 0 8px' }}>
              A 4K reference sheet — the product front-on, at real-world scale, plus its sub-forms
              (single-serve packets) and how it looks prepared. The AI reads this as the product's
              visual truth.
            </p>
            {sheetUrl && (
              <a href={sheetUrl} target="_blank" rel="noreferrer" style={{ display: 'block', marginBottom: 8 }}>
                <img
                  src={sheetUrl}
                  alt="Product sheet"
                  data-testid="img-product-sheet"
                  style={{ width: '100%', borderRadius: 10, border: '1px solid #E0DDD8', display: 'block' }}
                />
              </a>
            )}
            <div style={{ marginBottom: 8 }}>
              <InputField
                value={sizeSpecValue}
                onChange={setSizeSpecValue}
                placeholder='Real dimensions, e.g. "Pouch 8.5 x 5 in; stick packet 4 in"'
                testId="input-product-size-spec"
              />
              {sizeSpecValue.startsWith('ESTIMATED') && (
                <p style={{ fontSize: 11.5, color: '#B45309', margin: '4px 0 0' }}>
                  Estimated by AI from the photo — type the real dimensions to make it truth.
                  Estimates are never used as claims in ad copy.
                </p>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={handleCreateSheet}
                disabled={sheetBusy}
                data-testid="button-create-product-sheet"
                style={{
                  padding: '8px 14px',
                  borderRadius: 10,
                  border: '1px solid #E0DDD8',
                  background: 'white',
                  fontSize: 13,
                  color: '#3D3A36',
                  cursor: sheetBusy ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {sheetBusy ? <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" /> : null}
                {sheetBusy ? 'Generating sheet…' : sheetUrl ? 'Regenerate Product Sheet' : 'Create Product Sheet'}
              </button>
              {sheetBusy && (
                <span style={{ fontSize: 12, color: '#6B6760' }}>
                  Rendering 4K panels — this takes a few minutes.
                </span>
              )}
            </div>
            {sheetError && <p style={{ fontSize: 12, color: '#DC2626', marginTop: 6 }}>{sheetError}</p>}
            {sheetSuggestions.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <p style={{ fontSize: 12, color: '#6B6760', margin: '0 0 6px' }}>
                  Suggested state notes from the sheet — tap to add:
                </p>
                {sheetSuggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setStateDescriptions((prev) => [...prev, s]);
                      setSheetSuggestions((prev) => prev.filter((_, j) => j !== i));
                    }}
                    data-testid={`button-add-suggested-state-${i}`}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '7px 10px',
                      marginBottom: 6,
                      borderRadius: 8,
                      border: '1px dashed #C9C4BC',
                      background: 'white',
                      fontSize: 12.5,
                      color: '#3D3A36',
                      cursor: 'pointer',
                    }}
                  >
                    + {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div>
          <FieldLabel>Product images (optional)</FieldLabel>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleImageUpload}
            data-testid="input-canvas-product-image-upload"
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {photos.map((photo, i) => (
              <div
                key={i}
                style={{ position: 'relative', width: 64, height: 64, borderRadius: 10, overflow: 'hidden', border: '1px solid #E0DDD8', flexShrink: 0 }}
              >
                <img src={photo} alt={`Product ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button
                  onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))}
                  style={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.55)',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <X style={{ width: 10, height: 10 }} />
                </button>
              </div>
            ))}
            <button
              data-testid="button-canvas-upload-product-image"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              style={{
                width: 64,
                height: 64,
                borderRadius: 10,
                border: '2px dashed #D4D0C8',
                background: '#F7F6F3',
                cursor: isUploading ? 'not-allowed' : 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                flexShrink: 0,
              }}
            >
              {isUploading ? (
                <Loader2 style={{ width: 16, height: 16, color: '#8C8578' }} className="animate-spin" />
              ) : (
                <>
                  <Upload style={{ width: 16, height: 16, color: '#8C8578' }} />
                  <span style={{ fontSize: 10, color: '#8C8578' }}>Upload</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      <div style={{ padding: '14px 24px', borderTop: '1px solid #E8E4DC', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <GhostButton onClick={onClose}>Cancel</GhostButton>
        <PrimaryButton onClick={handleSave} loading={isSaving} disabled={!name} testId="button-canvas-save-product">
          {product ? 'Save changes' : 'Add product'}
        </PrimaryButton>
      </div>
    </ModalBackdrop>
  );
}

function EditBrandModal({ brand, onClose, onSuccess }: { brand: Brand; onClose: () => void; onSuccess: () => void }) {
  const originalBrand = useRef(brand);
  const [name, setName] = useState(brand.name || '');
  const [industry, setIndustry] = useState(brand.industry || '');
  const [website, setWebsite] = useState(brand.website || '');
  const [description, setDescription] = useState(brand.description || '');
  const [toneOfVoice, setToneOfVoice] = useState(brand.toneOfVoice || '');
  const [logo, setLogo] = useState<string | null>(brand.logo || null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleLogoUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/upload', { method: 'POST', body: formData });
      if (!res.ok) throw new Error('Upload failed');
      const { url } = await res.json();
      setLogo(url);
    } catch {
      toast({ title: 'Upload failed', description: 'Could not upload logo.', variant: 'destructive' });
    } finally {
      setIsUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  }, [toast]);

  const handleSave = async () => {
    if (!name) return;
    setIsSaving(true);
    try {
      const response = await apiRequest('PATCH', `/api/brands/${brand.id}`, legacyBrandChanges(originalBrand.current, { name, industry, website, description, toneOfVoice, logo }));
      const updated = await response.json();
      queryClient.setQueryData(['/api/brands', brand.id], updated);
      queryClient.invalidateQueries({ queryKey: ['/api/brands'] });
      toast({ title: 'Brand updated', description: `"${name}" has been updated.` });
      onSuccess();
      onClose();
    } catch {
      toast({ title: 'Error', description: 'Could not update brand.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalBackdrop onClose={onClose}>
      <ModalHeader title="Edit brand" onClose={onClose} />
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Logo upload */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <input
            ref={logoInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleLogoUpload}
            data-testid="input-canvas-edit-brand-logo"
          />
          <div
            onClick={() => !isUploadingLogo && logoInputRef.current?.click()}
            data-testid="button-canvas-edit-brand-logo"
            style={{
              width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
              border: logo ? '2px solid #E0DDD8' : '2px dashed #C8C4BC',
              background: logo ? '#fff' : '#F7F6F3',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: isUploadingLogo ? 'wait' : 'pointer',
              overflow: 'hidden', position: 'relative',
            }}
          >
            {isUploadingLogo ? (
              <div style={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid #6366F1', borderTopColor: 'transparent', animation: 'spin 0.7s linear infinite' }} />
            ) : logo ? (
              <>
                <img src={logo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6 }} />
                <div style={{
                  position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: 0, transition: 'opacity 0.15s',
                }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '0')}
                >
                  <Upload style={{ width: 16, height: 16, color: '#fff' }} />
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Upload style={{ width: 16, height: 16, color: '#8C8578' }} />
                <span style={{ fontSize: 9, color: '#8C8578', fontWeight: 500 }}>Logo</span>
              </div>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <FieldLabel required>Brand name</FieldLabel>
            <InputField value={name} onChange={setName} placeholder="Brand name" testId="input-canvas-edit-brand-name" />
          </div>
        </div>
        <div>
          <FieldLabel>Industry</FieldLabel>
          <InputField value={industry} onChange={setIndustry} placeholder="e.g. E-commerce" testId="input-canvas-edit-brand-industry" />
        </div>
        <div>
          <FieldLabel>Website</FieldLabel>
          <InputField value={website} onChange={setWebsite} placeholder="https://..." testId="input-canvas-edit-brand-website" />
        </div>
        <div>
          <FieldLabel>Description</FieldLabel>
          <textarea
            data-testid="input-canvas-edit-brand-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brand description"
            rows={3}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: 10,
              border: '1px solid #E0DDD8',
              background: '#F7F6F3',
              fontSize: 13.5,
              color: '#1A1A1A',
              outline: 'none',
              resize: 'vertical',
              boxSizing: 'border-box',
              fontFamily: 'inherit',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = '#B5B0A8')}
            onBlur={(e) => (e.currentTarget.style.borderColor = '#E0DDD8')}
          />
        </div>
        <div>
          <FieldLabel>Tone of voice</FieldLabel>
          <InputField value={toneOfVoice} onChange={setToneOfVoice} placeholder="e.g. Friendly, professional" testId="input-canvas-edit-brand-tone" />
        </div>
      </div>
      <div style={{ padding: '14px 24px', borderTop: '1px solid #E8E4DC', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <GhostButton onClick={onClose}>Cancel</GhostButton>
        <PrimaryButton onClick={handleSave} loading={isSaving} disabled={!name} testId="button-canvas-save-brand">
          Save changes
        </PrimaryButton>
      </div>
    </ModalBackdrop>
  );
}

function DeleteConfirmModal({
  title,
  message,
  onClose,
  onConfirm,
  isLoading,
}: {
  title: string;
  message: string;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}) {
  return (
    <ModalBackdrop onClose={onClose}>
      <ModalHeader title={title} onClose={onClose} />
      <div style={{ padding: '20px 24px' }}>
        <p style={{ fontSize: 14, color: '#6B6760', lineHeight: 1.6 }}>{message}</p>
      </div>
      <div style={{ padding: '14px 24px', borderTop: '1px solid #E8E4DC', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <GhostButton onClick={onClose}>Cancel</GhostButton>
        <DangerButton onClick={onConfirm} loading={isLoading} testId="button-canvas-confirm-delete">
          Delete
        </DangerButton>
      </div>
    </ModalBackdrop>
  );
}

function IntelScanModal({
  brandId,
  onClose,
}: {
  brandId: number;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleSearch = async () => {
    if (!search.trim()) return;
    setIsSearching(true);
    try {
      const res = await fetch(`/api/pages/search?q=${encodeURIComponent(search.trim())}`);
      const data = await res.json();
      setResults(data.pages || []);
    } catch {
      toast({ title: 'Search failed', description: 'Could not search pages.', variant: 'destructive' });
    } finally {
      setIsSearching(false);
    }
  };

  const handleStartScan = async (pageId: string, pageName: string) => {
    try {
      await apiRequest('POST', `/api/brands/${brandId}/intelligence/scan`, { pageId, pageName });
      queryClient.invalidateQueries({ queryKey: ['/api/brands', brandId, 'intelligence'] });
      toast({ title: 'Scan started', description: `Scanning ${pageName}'s ad library…` });
      onClose();
    } catch {
      toast({ title: 'Scan failed', description: 'Could not start the scan.', variant: 'destructive' });
    }
  };

  return (
    <ModalBackdrop onClose={onClose}>
      <ModalHeader title="Scan Ad Library" onClose={onClose} />
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <InputField
            value={search}
            onChange={setSearch}
            placeholder="Search for a brand (e.g. Nike, Apple)"
            testId="input-canvas-intel-search"
          />
          <button
            onClick={handleSearch}
            disabled={isSearching || !search.trim()}
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              border: 'none',
              background: '#1A1A1A',
              color: 'white',
              fontSize: 13,
              cursor: search.trim() && !isSearching ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              whiteSpace: 'nowrap',
            }}
            data-testid="button-canvas-intel-search"
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          >
            {isSearching ? <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" /> : <Search style={{ width: 13, height: 13 }} />}
            Search
          </button>
        </div>
        {results.length > 0 && (
          <div style={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {results.map((page: any) => (
              <button
                key={page.page_id}
                data-testid={`button-canvas-select-page-${page.page_id}`}
                onClick={() => handleStartScan(page.page_id, page.page_name)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: '1px solid #E8E4DC',
                  background: '#FAFAF8',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F0EDE8')}
                onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#FAFAF8')}
              >
                {page.page_logo_url ? (
                  <img src={page.page_logo_url} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#E8E4DC', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#8C8578' }}>
                    {page.page_name?.charAt(0) || '?'}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 500, color: '#1A1A1A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{page.page_name}</p>
                  {page.category && <p style={{ fontSize: 11, color: '#8C8578' }}>{page.category}</p>}
                </div>
                {page.ad_count > 0 && (
                  <span style={{ fontSize: 11, color: '#8C8578', background: '#E8E4DC', padding: '2px 7px', borderRadius: 10 }}>{page.ad_count} ads</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </ModalBackdrop>
  );
}

// v7.08 — `scanning` (Dan): while the 8-phase onboarding job is still running
// the tile reads as NOT READY — content dimmed, an "Analyzing Brand" pill in
// place of the status badge — but stays fully clickable so the user can open
// it and watch the phase-by-phase bar inside.
function BrandCard({ brand, onClick, scanning }: { brand: Brand; onClick: () => void; scanning?: boolean }) {
  return (
    <div
      data-testid={`card-brand-${brand.id}`}
      data-scanning={scanning ? 'true' : undefined}
      onClick={onClick}
      style={{
        background: scanning ? '#F4F2EE' : '#FAFAF8',
        border: `1px solid ${scanning ? '#E0DDD8' : '#E8E4DC'}`,
        borderRadius: 16,
        padding: '18px 18px 14px',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        transition: 'box-shadow 150ms, border-color 150ms',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
        (e.currentTarget as HTMLDivElement).style.borderColor = '#D4D0C8';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
        (e.currentTarget as HTMLDivElement).style.borderColor = '#E8E4DC';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, opacity: scanning ? 0.55 : 1, transition: 'opacity 200ms' }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: '#F0EDE8',
            border: '1px solid #E8E4DC',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          {brand.logo ? (
            <img src={brand.logo} alt={brand.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6 }} />
          ) : (
            <span style={{ fontSize: 18, fontWeight: 700, color: '#8C8578' }}>{brand.name.charAt(0)}</span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
            <span data-testid={`text-brand-name-${brand.id}`} style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {brand.name}
            </span>
          </div>
          {brand.industry && <p style={{ fontSize: 12, color: '#8C8578' }}>{brand.industry}</p>}
        </div>
        {scanning ? (
          <span
            data-testid={`badge-brand-analyzing-${brand.id}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
              padding: '3px 9px', borderRadius: 999, background: '#F5F3FF',
              border: '1px solid #E0D9F5', color: '#7C3AED', fontSize: 11, fontWeight: 600,
            }}
          >
            <Loader2 style={{ width: 11, height: 11 }} className="animate-spin" />
            Analyzing Brand
          </span>
        ) : (
          <StatusBadge status={brand.status} />
        )}
      </div>

      <div style={{ opacity: scanning ? 0.55 : 1, transition: 'opacity 200ms' }}>
        {brand.colors && brand.colors.length > 0 && <PaletteSwatches colors={brand.colors} />}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto' }}>
        <span style={{ fontSize: 12, color: '#B5B0A8', opacity: scanning ? 0.55 : 1 }}>{brand.website || ''}</span>
        {/* v7.50 (Dan): no "View progress" link on an analyzing tile — the
            pill already says it; the tile itself stays clickable. */}
        {!scanning && (
          <span
            data-testid={`link-brand-view-${brand.id}`}
            style={{ fontSize: 12.5, fontWeight: 600, color: '#3D3A36', display: 'flex', alignItems: 'center', gap: 3 }}
          >
            View <ChevronRight style={{ width: 12, height: 12 }} />
          </span>
        )}
      </div>
    </div>
  );
}

function AddBrandCard({ onClick }: { onClick: () => void }) {
  return (
    <div
      data-testid="card-add-brand"
      onClick={onClick}
      style={{
        border: '2px dashed #D4D0C8',
        borderRadius: 16,
        padding: '18px 18px 14px',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        minHeight: 140,
        transition: 'border-color 150ms, background 150ms',
        background: 'transparent',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = '#F7F6F3';
        (e.currentTarget as HTMLDivElement).style.borderColor = '#B5B0A8';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'transparent';
        (e.currentTarget as HTMLDivElement).style.borderColor = '#D4D0C8';
      }}
    >
      <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#F0EDE8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Plus style={{ width: 16, height: 16, color: '#6B6760' }} />
      </div>
      <span style={{ fontSize: 13, fontWeight: 500, color: '#8C8578' }}>Analyze new brand</span>
    </div>
  );
}

function TagPill({ label, color }: { label: string; color: string }) {
  return (
    <span style={{ fontSize: 11.5, padding: '3px 10px', borderRadius: 20, background: color, color: '#1A1A1A', fontWeight: 500 }}>
      {label}
    </span>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#FAFAF8', border: '1px solid #E8E4DC', borderRadius: 14, padding: '16px 18px' }}>
      <p style={{ fontSize: 12, fontWeight: 600, color: '#6B6760', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>{title}</p>
      {children}
    </div>
  );
}

function StatPill({ label, value }: { label: string; value: number | string | undefined }) {
  return (
    <div style={{ flex: 1, minWidth: 0, background: '#F7F6F3', border: '1px solid #E8E4DC', borderRadius: 12, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`} style={{ fontSize: 20, fontWeight: 700, color: '#1A1A1A', lineHeight: 1 }}>
        {value === undefined ? '–' : value}
      </span>
      <span style={{ fontSize: 11, fontWeight: 500, color: '#8C8578', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
    </div>
  );
}

type OnboardingPhaseEntry = {
  id: string;
  name: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  error?: string;
};

type OnboardingJob = {
  id: number;
  brand_id: number;
  // v2.03 — 'completed_with_errors' was missing here even though the server
  // emits it; the gap hid a dead status comparison (TS2367) and let the scan
  // card spin forever on warn-state jobs.
  status: 'pending' | 'running' | 'completed' | 'completed_with_errors' | 'failed';
  phases?: OnboardingPhaseEntry[];
  error_message?: string | null;
};

const ONBOARDING_PHASE_TEXT: Record<string, string> = {
  brand_dna: "Ingesting brand DNA...",
  competitor_discovery: "Discovering competitors...",
  competitor_research: "Researching competitor positioning...",
  brand_ads: "Scanning brand ad library...",
  competitor_ads: "Scanning competitor ads...",
  visual_identity: "Analyzing visual identity...",
  competitor_insights: "Extracting competitor sentiment...",
  generate_ad_angles: "Generating ad angle library...",
  brand_identity_lock: "Locking brand visual identity...",
};

function OnboardingScanCard({ job, onRetry }: { job: OnboardingJob; onRetry: () => void }) {
  const isActive = job.status === 'pending' || job.status === 'running';
  const isFailed = job.status === 'failed';
  // v2.03 — completed_with_errors is a TERMINAL state (some phases failed,
  // the rest finished). It used to fall through to the default branch and
  // render a spinner + "Building Brand Intelligence…" forever — users read
  // a finished scan as permanently stuck.
  const isWarn = job.status === 'completed_with_errors';
  if (job.status === 'completed') return null;

  const phases: OnboardingPhaseEntry[] = Array.isArray(job.phases) ? job.phases : [];
  const completedCount = phases.filter((p) => p.status === 'completed').length;
  const totalPhases = phases.length || 6;
  const progress = totalPhases > 0 ? Math.round((completedCount / totalPhases) * 100) : 0;
  const runningPhase = phases.find((p) => p.status === 'running');
  const failedPhases = phases.filter((p) => p.status === 'failed');
  const currentPhaseText = runningPhase
    ? (ONBOARDING_PHASE_TEXT[runningPhase.id] || runningPhase.name)
    : "Initializing...";

  return (
    <div
      data-testid="onboarding-scan-card"
      style={{
        borderRadius: 14,
        border: `1px solid ${isFailed ? '#FECACA' : isWarn ? '#FDE68A' : '#E0D9F5'}`,
        background: isFailed ? '#FFF5F5' : isWarn ? '#FFFBEB' : '#F5F3FF',
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
      }}
    >
      <div style={{ paddingTop: 2 }}>
        {isFailed ? (
          <AlertCircle style={{ width: 18, height: 18, color: '#DC2626', flexShrink: 0 }} />
        ) : isWarn ? (
          <AlertCircle style={{ width: 18, height: 18, color: '#D97706', flexShrink: 0 }} />
        ) : (
          <Loader2 style={{ width: 18, height: 18, color: '#7C3AED', flexShrink: 0 }} className="animate-spin" />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: isFailed ? '#991B1B' : isWarn ? '#92400E' : '#4C1D95', margin: 0 }}>
            {isFailed ? 'Intelligence Scan Failed' : isWarn ? `Scan finished — ${completedCount}/${totalPhases} steps OK` : 'Building Brand Intelligence…'}
          </p>
          {isActive && (
            <span style={{ fontSize: 12, fontWeight: 600, color: '#7C3AED' }}>{progress}%</span>
          )}
        </div>
        {isActive && (
          <>
            <div style={{
              width: '100%',
              height: 5,
              background: '#E0D9F5',
              borderRadius: 3,
              overflow: 'hidden',
              marginTop: 6,
              marginBottom: 6,
            }}>
              <div style={{
                height: '100%',
                width: `${Math.max(progress, 3)}%`,
                background: '#7C3AED',
                borderRadius: 3,
                transition: 'width 0.7s ease-out',
              }} />
            </div>
            <p style={{ fontSize: 12, color: '#7C3AED', margin: 0 }}>
              {currentPhaseText}
            </p>
          </>
        )}
        {isFailed && job.error_message && (
          <p style={{ fontSize: 11.5, color: '#B91C1C', marginTop: 2 }}>{job.error_message}</p>
        )}
        {isWarn && (
          <div style={{ marginTop: 4 }}>
            {failedPhases.length === 0 ? (
              <p style={{ fontSize: 11.5, color: '#92400E', margin: 0 }}>Some steps reported warnings — everything else is ready to use.</p>
            ) : (
              failedPhases.map((p) => (
                <p key={p.id} style={{ fontSize: 11.5, color: '#92400E', margin: '2px 0 0' }}>
                  {p.id === 'brand_identity_lock'
                    ? 'Brand Identity Lock skipped — no Facebook ads found for this brand. Upload 5+ brand images in the Brand Identity tab and hit Analyze to enable it.'
                    : `${p.name || p.id}: ${p.error || 'did not complete'}`}
                </p>
              ))
            )}
          </div>
        )}
      </div>
      {isActive && (
        <span style={{ fontSize: 12, color: '#7C3AED', fontWeight: 500, flexShrink: 0, paddingTop: 2 }}>
          {completedCount}/{totalPhases}
        </span>
      )}
      {isFailed && (
        <button
          data-testid="button-retry-onboarding"
          onClick={onRetry}
          style={{
            padding: '6px 14px',
            borderRadius: 10,
            background: '#DC2626',
            color: 'white',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            border: 'none',
            flexShrink: 0,
          }}
        >
          Retry
        </button>
      )}
    </div>
  );
}

export function OverviewTab({ brand, brandId, adAccountId }: { brand: Brand; brandId: number; adAccountId?: number }) {
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deletingProductId, setDeletingProductId] = useState<number | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: onboardingJob } = useQuery<OnboardingJob | null>({
    queryKey: ['/api/brands', brandId, 'onboarding'],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/onboarding`);
      if (!res.ok) return null;
      return res.json();
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && (data.status === 'pending' || data.status === 'running')) return 3000;
      return false;
    },
    enabled: !!brandId,
  });

  // Refresh stats when onboarding finishes
  const onboardingStatus = onboardingJob?.status;
  useEffect(() => {
    if (onboardingStatus === 'completed' || onboardingStatus === 'completed_with_errors') {
      queryClient.invalidateQueries({ queryKey: ['/api/heartbeat/brands', brandId, 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['/api/brands', brandId] });
    }
  }, [onboardingStatus, brandId, queryClient]);

  const retryOnboarding = async () => {
    try {
      await apiRequest('POST', `/api/brands/${brandId}/onboarding/start`);
      queryClient.invalidateQueries({ queryKey: ['/api/brands', brandId, 'onboarding'] });
      toast({ title: 'Scan restarted', description: 'Building brand intelligence…' });
    } catch {
      toast({ title: 'Failed to restart scan', variant: 'destructive' });
    }
  };

  const { data: products = [], isLoading: isProductsLoading } = useQuery<Product[]>({
    queryKey: ['/api/brands', brandId, 'products'],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/products`);
      return res.json();
    },
  });

  // Real angle count: copy-set angles + legacy brand-level angles. The old
  // stat read the DEPRECATED saved_angles table and showed 0 forever once
  // angles moved to ad_angles/copy sets. Query key matches AdAnglesPanel's
  // list so the two share a cache entry.
  const { data: angleSetsData } = useQuery<{ sets: Array<{ angleCount: number | null }>; legacyCount: number }>({
    queryKey: ['angle-sets', brandId],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/angle-sets`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load copy sets');
      return res.json();
    },
  });
  const totalAngles =
    (angleSetsData?.sets ?? []).reduce((sum, s) => sum + (s.angleCount ?? 0), 0) +
    (angleSetsData?.legacyCount ?? 0);

  const { data: heartbeatStats } = useQuery<{ totalChunks: number }>({
    queryKey: ['/api/heartbeat/brands', brandId, 'stats'],
    queryFn: async () => {
      const res = await fetch(`/api/heartbeat/brands/${brandId}/stats`);
      return res.json();
    },
    enabled: !!brandId,
  });

  const deleteProductMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest('DELETE', `/api/products/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/brands', brandId, 'products'] });
      toast({ title: 'Product deleted' });
      setDeletingProductId(null);
    },
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Onboarding scan status card */}
      {onboardingJob && onboardingJob.status !== 'completed' && (
        <OnboardingScanCard job={onboardingJob} onRetry={retryOnboarding} />
      )}
      {/* Re-scan button when scan is already complete */}
      {onboardingJob && onboardingJob.status === 'completed' && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            data-testid="button-rescan-brand"
            onClick={retryOnboarding}
            style={{
              border: '1px solid #E5E2DC',
              borderRadius: 7,
              padding: '5px 12px',
              background: 'white',
              fontSize: 12,
              color: '#8C8578',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <RefreshCw style={{ width: 11, height: 11 }} />
            Re-scan brand
          </button>
        </div>
      )}
      {/* Stats row */}
      <div style={{ display: 'flex', gap: 10 }}>
        <StatPill label="Ad Angles" value={totalAngles} />
        <StatPill label="Products" value={products.length} />
        <StatPill label="DNA Nodes" value={heartbeatStats?.totalChunks} />
      </div>
      <IdentityEditor key={brandId} record={brand} onUpdated={updated => queryClient.setQueryData(['/api/brands', brandId], updated)} />

      {/* Products */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Package style={{ width: 16, height: 16, color: '#8C8578' }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A' }}>Products</span>
            <span style={{ fontSize: 12, color: '#8C8578', background: '#E8E4DC', padding: '1px 8px', borderRadius: 10 }}>{products.length}</span>
          </div>
          <span style={{ fontSize: 11.5, color: '#B5B0A8' }}>Add &amp; manage in the Products tab</span>
        </div>
        {isProductsLoading ? (
          <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 6 }}>
            {[1, 2, 3].map((i) => (
              <div key={i} style={{ width: 160, height: 80, background: '#F0EDE8', borderRadius: 12, flexShrink: 0 }} className="animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div
            style={{
              border: '2px dashed #D4D0C8',
              borderRadius: 14,
              padding: '28px',
              textAlign: 'center',
              cursor: 'pointer',
            }}
            onClick={() => setShowAddProduct(true)}
          >
            <Package style={{ width: 28, height: 28, color: '#D4D0C8', margin: '0 auto 8px' }} />
            <p style={{ fontSize: 13, color: '#8C8578', fontWeight: 500 }}>No products yet</p>
            <p style={{ fontSize: 12, color: '#B5B0A8', marginTop: 3 }}>Add products to use in ad creatives</p>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 6 }}>
            {products.map((p) => (
              <div
                key={p.id}
                data-testid={`card-product-${p.id}`}
                style={{
                  width: 150,
                  flexShrink: 0,
                  background: '#FAFAF8',
                  border: '1px solid #E8E4DC',
                  borderRadius: 14,
                  padding: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                <p data-testid={`text-product-name-${p.id}`} style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</p>
                {p.price && <p style={{ fontSize: 11, color: '#8C8578' }}>{p.price}</p>}
                <div style={{ display: 'flex', gap: 5, marginTop: 'auto' }}>
                  <button
                    data-testid={`button-edit-product-${p.id}`}
                    onClick={() => setEditingProduct(p)}
                    style={{ fontSize: 11, color: '#6B6760', background: '#F0EDE8', border: 'none', borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}
                  >
                    Edit
                  </button>
                  <button
                    data-testid={`button-delete-product-${p.id}`}
                    onClick={() => setDeletingProductId(p.id)}
                    style={{ fontSize: 11, color: '#DC2626', background: '#FEF2F2', border: 'none', borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAddProduct && (
        <AddProductModal brandId={brandId} onClose={() => setShowAddProduct(false)} onSuccess={() => {}} />
      )}
      {editingProduct && (
        <AddProductModal brandId={brandId} product={editingProduct} onClose={() => setEditingProduct(null)} onSuccess={() => setEditingProduct(null)} />
      )}
      {deletingProductId !== null && (
        <DeleteConfirmModal
          title="Delete product"
          message="Are you sure you want to delete this product? This cannot be undone."
          onClose={() => setDeletingProductId(null)}
          onConfirm={() => deleteProductMutation.mutate(deletingProductId!)}
          isLoading={deleteProductMutation.isPending}
        />
      )}
    </div>
  );
}

function AdAnglesTab({ brandId }: { brandId: number }) {
  return <AdAnglesPanel brandId={brandId} />;
}

function AdIntelligenceTab({ brandId }: { brandId: number }) {
  const [showScanModal, setShowScanModal] = useState(false);
  const queryClient = useQueryClient();

  const { data: intelligence, isLoading } = useQuery<IntelligenceData | null>({
    queryKey: ['/api/brands', brandId, 'intelligence'],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/intelligence`);
      if (!res.ok) return null;
      return res.json();
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && (data.status === 'scanning' || data.status === 'analyzing' || data.status === 'synthesizing')) {
        return 3000;
      }
      return false;
    },
  });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[1, 2].map((i) => (
          <div key={i} style={{ height: 100, background: '#F0EDE8', borderRadius: 14 }} className="animate-pulse" />
        ))}
      </div>
    );
  }

  const isScanning = intelligence?.status === 'scanning' || intelligence?.status === 'analyzing' || intelligence?.status === 'synthesizing';

  if (!intelligence) {
    return (
      <>
        <div style={{ textAlign: 'center', padding: '48px 24px', border: '2px dashed #D4D0C8', borderRadius: 16 }}>
          <Brain style={{ width: 32, height: 32, color: '#D4D0C8', margin: '0 auto 10px' }} />
          <p style={{ fontSize: 14, fontWeight: 500, color: '#8C8578' }}>No ad intelligence scan yet</p>
          <p style={{ fontSize: 12, color: '#B5B0A8', marginTop: 4, marginBottom: 16 }}>Scan a Facebook Ad Library page to analyze ad strategy</p>
          <button
            data-testid="button-canvas-start-intel-scan"
            onClick={() => setShowScanModal(true)}
            style={{
              padding: '9px 20px',
              borderRadius: 10,
              background: '#1A1A1A',
              color: 'white',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              border: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Search style={{ width: 14, height: 14 }} />
            Scan Ad Library
          </button>
        </div>
        {showScanModal && <IntelScanModal brandId={brandId} onClose={() => setShowScanModal(false)} />}
      </>
    );
  }

  if (isScanning) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px', border: '1px solid #E8E4DC', borderRadius: 16, background: '#FAFAF8' }}>
        <Loader2 style={{ width: 32, height: 32, color: '#6366F1', margin: '0 auto 10px' }} className="animate-spin" />
        <p style={{ fontSize: 14, fontWeight: 500, color: '#3D3A36' }}>
          {intelligence.status === 'scanning' && 'Fetching ads from Facebook Ad Library…'}
          {intelligence.status === 'analyzing' && `Analyzing ${intelligence.totalAdsScanned} ads with AI…`}
          {intelligence.status === 'synthesizing' && 'Synthesizing strategic intelligence…'}
        </p>
        <p style={{ fontSize: 12, color: '#8C8578', marginTop: 4 }}>This may take 1–2 minutes</p>
      </div>
    );
  }

  if (intelligence.status === 'failed') {
    return (
      <>
        <div style={{ textAlign: 'center', padding: '48px 24px', border: '2px dashed #FCA5A5', borderRadius: 16, background: '#FFF5F5' }}>
          <p style={{ fontSize: 14, fontWeight: 500, color: '#DC2626' }}>Scan failed</p>
          <p style={{ fontSize: 12, color: '#F87171', marginTop: 4, marginBottom: 16 }}>{intelligence.synthesisNarrative}</p>
          <button
            onClick={() => setShowScanModal(true)}
            style={{
              padding: '9px 20px',
              borderRadius: 10,
              border: '1px solid #FCA5A5',
              background: 'white',
              color: '#DC2626',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <RefreshCw style={{ width: 13, height: 13 }} />
            Try Again
          </button>
        </div>
        {showScanModal && <IntelScanModal brandId={brandId} onClose={() => setShowScanModal(false)} />}
      </>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Brain style={{ width: 18, height: 18, color: '#6366F1' }} />
            <span style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A' }}>{intelligence.adAccountName}</span>
            <span style={{ fontSize: 11, background: '#EDE9FE', color: '#5B21B6', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
              {intelligence.totalAdsScanned} ads scanned
            </span>
          </div>
          <button
            data-testid="button-canvas-rescan-intel"
            onClick={() => setShowScanModal(true)}
            style={{
              padding: '7px 14px',
              borderRadius: 10,
              border: '1px solid #E0DDD8',
              background: 'white',
              fontSize: 12.5,
              color: '#3D3A36',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <RefreshCw style={{ width: 12, height: 12 }} />
            Re-scan
          </button>
        </div>

        {intelligence.synthesisNarrative && (
          <SectionCard title="Strategic Summary">
            <p style={{ fontSize: 13.5, color: '#3D3A36', lineHeight: 1.65, whiteSpace: 'pre-line' }}>{intelligence.synthesisNarrative}</p>
          </SectionCard>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {intelligence.dominantAdStyles && intelligence.dominantAdStyles.length > 0 && (
            <SectionCard title="Dominant Ad Styles">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {intelligence.dominantAdStyles.map((s: string, i: number) => (
                  <TagPill key={i} label={s} color="#EDE9FE" />
                ))}
              </div>
            </SectionCard>
          )}
          {intelligence.dominantAngles && intelligence.dominantAngles.length > 0 && (
            <SectionCard title="Dominant Angles">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {intelligence.dominantAngles.map((a: string, i: number) => (
                  <TagPill key={i} label={a} color="#DBEAFE" />
                ))}
              </div>
            </SectionCard>
          )}
          {intelligence.messagingThemes && intelligence.messagingThemes.length > 0 && (
            <SectionCard title="Messaging Themes">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {intelligence.messagingThemes.map((t: string, i: number) => (
                  <TagPill key={i} label={t} color="#D1FAE5" />
                ))}
              </div>
            </SectionCard>
          )}
          {intelligence.offerPatterns && intelligence.offerPatterns.length > 0 && (
            <SectionCard title="Offer Patterns">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {intelligence.offerPatterns.map((p: string, i: number) => (
                  <TagPill key={i} label={p} color="#FEF3C7" />
                ))}
              </div>
            </SectionCard>
          )}
          {intelligence.provenHooks && intelligence.provenHooks.length > 0 && (
            <SectionCard title="Proven Hooks">
              <ul style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {intelligence.provenHooks.map((h: string, i: number) => (
                  <li key={i} style={{ fontSize: 12.5, color: '#3D3A36', lineHeight: 1.55 }}>"{h}"</li>
                ))}
              </ul>
            </SectionCard>
          )}
          {intelligence.audienceSignals && intelligence.audienceSignals.length > 0 && (
            <SectionCard title="Audience Signals">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {intelligence.audienceSignals.map((s: string, i: number) => (
                  <TagPill key={i} label={s} color="#FCE7F3" />
                ))}
              </div>
            </SectionCard>
          )}
        </div>

        {intelligence.ads && intelligence.ads.length > 0 && (
          <div style={{ paddingBottom: 32 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A', marginBottom: 10 }}>
              Brand Ads ({intelligence.ads.length})
            </p>
            <BrandAdGallery ads={intelligence.ads} />
          </div>
        )}
      </div>
      {showScanModal && <IntelScanModal brandId={brandId} onClose={() => setShowScanModal(false)} />}
    </>
  );
}

function DetailSkeleton() {
  const shimmer = { background: 'linear-gradient(90deg, #F0EDE8 25%, #E8E4DC 50%, #F0EDE8 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <style>{`@keyframes shimmer { 0% { background-position: 200% 0 } 100% { background-position: -200% 0 } }`}</style>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, ...shimmer }} />
        <div style={{ width: 52, height: 52, borderRadius: 14, ...shimmer }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ width: '40%', height: 18, borderRadius: 6, ...shimmer }} />
          <div style={{ width: '25%', height: 13, borderRadius: 6, ...shimmer }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        {[1, 2, 3].map((i) => <div key={i} style={{ flex: 1, height: 64, borderRadius: 12, ...shimmer }} />)}
      </div>
      <div style={{ height: 200, borderRadius: 14, ...shimmer }} />
    </div>
  );
}

function BrandDetailView({ brandId, initialBrand, onBack }: { brandId: number; initialBrand: Brand; onBack: () => void }) {
  // v3.91 — honor a one-shot deep-link tab (welcome "Ad Angles" shortcut sets
  // pendingBrandsTab='angles' before opening this view), then clear it.
  const [tab, setTab] = useState<'overview' | 'angles' | 'competitors' | 'intelligence' | 'identity' | 'products'>(() => {
    const pending = useCanvasStore.getState().pendingBrandsTab;
    if (pending) { useCanvasStore.setState({ pendingBrandsTab: null }); return pending; }
    return 'overview';
  });
  const [productsAddOpen, setProductsAddOpen] = useState(false);
  const [productsEditTarget, setProductsEditTarget] = useState<Product | null>(null);
  const [productsDeleteTarget, setProductsDeleteTarget] = useState<Product | null>(null);
  const [productsRefresh, setProductsRefresh] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [visualIdentityStatus, setVisualIdentityStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: brand, isLoading: isBrandLoading } = useQuery<Brand>({
    queryKey: ['/api/brands', brandId],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}`);
      if (!res.ok) throw new Error('Failed to load brand');
      return res.json();
    },
    initialData: initialBrand,
    staleTime: 30_000,
  });

  const deleteBrandMutation = useMutation({
    mutationFn: async () => {
      await apiRequest('DELETE', `/api/brands/${brandId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/brands'] });
      toast({ title: 'Brand deleted' });
      onBack();
    },
  });

  const deleteProductFromTabMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest('DELETE', `/api/products/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/brands', brandId, 'products'] });
      toast({ title: 'Product deleted' });
      setProductsDeleteTarget(null);
      setProductsRefresh((n) => n + 1);
    },
    onError: () => {
      toast({ title: 'Could not delete product', variant: 'destructive' });
    },
  });

  useEffect(() => {
    setVisualIdentityStatus('idle');
  }, [brandId]);

  const handleAnalyzeVisualIdentity = async () => {
    if (visualIdentityStatus === 'loading') return;
    setVisualIdentityStatus('loading');
    try {
      const res = await fetch(`/api/brands/${brandId}/analyze-visual-identity`, { method: 'POST' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Analysis failed');
      }
      setVisualIdentityStatus('success');
      queryClient.invalidateQueries({ queryKey: ['/api/brands', brandId] });
      toast({ title: 'Identity suggestions ready', description: 'Review the proposed values in Overview or Brand Identity. Approved values are unchanged.' });
    } catch (err: any) {
      setVisualIdentityStatus('error');
      toast({ title: 'Analysis Failed', description: err.message || 'Could not analyze visual identity.', variant: 'destructive' });
    }
  };

  const tabs = [
    { key: 'overview' as const, label: 'Overview', icon: Package },
    { key: 'angles' as const, label: 'Ad Angles', icon: Lightbulb },
    { key: 'competitors' as const, label: 'Competitors', icon: Users },
    { key: 'intelligence' as const, label: 'Ad Intelligence', icon: Brain },
    { key: 'identity' as const, label: 'Brand Identity', icon: Sparkles },
    { key: 'products' as const, label: 'Products', icon: Palette },
  ];

  if (isBrandLoading && !brand) {
    return <DetailSkeleton />;
  }

  const displayBrand = brand ?? initialBrand;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button
          data-testid="button-canvas-brand-back"
          onClick={onBack}
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: '#F0EDE8',
            border: '1px solid #E8E4DC',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#E8E4DC')}
          onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F0EDE8')}
        >
          <ArrowLeft style={{ width: 15, height: 15, color: '#3D3A36' }} />
        </button>

        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: 14,
            background: '#F0EDE8',
            border: '1px solid #E8E4DC',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          {displayBrand.logo ? (
            <img src={displayBrand.logo} alt={displayBrand.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6 }} />
          ) : (
            <span style={{ fontSize: 22, fontWeight: 700, color: '#8C8578' }}>{displayBrand.name.charAt(0)}</span>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 data-testid="text-canvas-brand-detail-name" style={{ fontSize: 18, fontWeight: 700, color: '#1A1A1A', margin: 0 }}>
            {displayBrand.name}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
            <span style={{ fontSize: 13, color: '#8C8578' }}>{displayBrand.industry || 'General'}</span>
            <StatusBadge status={displayBrand.status} />
            {displayBrand.website && (
              <a
                href={displayBrand.website.startsWith('http') ? displayBrand.website : `https://${displayBrand.website}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 12, color: '#6B6760', textDecoration: 'none' }}
              >
                <Link2 style={{ width: 11, height: 11 }} />
                {displayBrand.website}
              </a>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            data-testid="button-canvas-analyze-visual-identity"
            onClick={handleAnalyzeVisualIdentity}
            disabled={visualIdentityStatus === 'loading'}
            style={{
              padding: '7px 14px',
              borderRadius: 10,
              border: visualIdentityStatus === 'success' ? '1px solid #22c55e' : visualIdentityStatus === 'error' ? '1px solid #f87171' : '1px solid #E0DDD8',
              background: 'white',
              fontSize: 13,
              color: visualIdentityStatus === 'success' ? '#15803d' : visualIdentityStatus === 'error' ? '#dc2626' : '#3D3A36',
              cursor: visualIdentityStatus === 'loading' ? 'not-allowed' : 'pointer',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              opacity: visualIdentityStatus === 'loading' ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (visualIdentityStatus !== 'loading') (e.currentTarget as HTMLButtonElement).style.background = '#F0EDE8';
            }}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'white')}
          >
            {visualIdentityStatus === 'loading' ? (
              <><Loader2 style={{ width: 13, height: 13 }} className="animate-spin" />Analyzing...</>
            ) : visualIdentityStatus === 'success' ? (
              <>Visual Identity Analyzed ✓</>
            ) : visualIdentityStatus === 'error' ? (
              <>Analysis Failed — Retry</>
            ) : (
              <>Analyze Visual Identity</>
            )}
          </button>
          <button
            data-testid="button-canvas-edit-brand"
            onClick={() => setShowEditModal(true)}
            style={{
              padding: '7px 14px',
              borderRadius: 10,
              border: '1px solid #E0DDD8',
              background: 'white',
              fontSize: 13,
              color: '#3D3A36',
              cursor: 'pointer',
              fontWeight: 500,
            }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F0EDE8')}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'white')}
          >
            Edit
          </button>
          <button
            data-testid="button-canvas-delete-brand"
            onClick={() => setShowDeleteModal(true)}
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              border: '1px solid #FCA5A5',
              background: 'white',
              color: '#DC2626',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#FFF5F5')}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'white')}
          >
            <Trash2 style={{ width: 14, height: 14 }} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid #E8E4DC', marginBottom: 20 }}>
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            data-testid={`tab-canvas-brand-${key}`}
            onClick={() => setTab(key)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '9px 14px',
              fontSize: 13.5,
              fontWeight: tab === key ? 600 : 400,
              color: tab === key ? '#1A1A1A' : '#8C8578',
              background: 'transparent',
              border: 'none',
              borderBottom: tab === key ? '2px solid #1A1A1A' : '2px solid transparent',
              cursor: 'pointer',
              marginBottom: -1,
            } as React.CSSProperties}
          >
            <Icon style={{ width: 14, height: 14 }} />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'overview' && <OverviewTab brand={displayBrand} brandId={displayBrand.id} />}
      {tab === 'angles' && <AdAnglesTab brandId={displayBrand.id} />}
      {tab === 'competitors' && (
        <div style={{ margin: '0 -32px', height: 'calc(100vh - 200px)', overflow: 'hidden' }}>
          <CompetitorAnalysisContent hideHeader brandId={displayBrand.id} />
        </div>
      )}
      {tab === 'intelligence' && <AdIntelligenceTab brandId={displayBrand.id} />}
      {tab === 'identity' && (
        <div style={{ borderRadius: 12, border: '1px solid #E5E1D8', overflow: 'hidden', background: '#FAF7F0' }} data-testid="brand-identity-tab">
          <BrandIdentityTab brandId={displayBrand.id} />
        </div>
      )}
      {tab === 'products' && (
        <div style={{ borderRadius: 12, border: '1px solid #E5E1D8', overflow: 'hidden', background: '#FAF7F0' }} data-testid="product-identity-tab">
          <ProductIdentityTab
            brandId={displayBrand.id}
            refreshSignal={productsRefresh}
            onAddProduct={() => setProductsAddOpen(true)}
            onEditProduct={(p) => setProductsEditTarget(p as unknown as Product)}
            onDeleteProduct={(p) => setProductsDeleteTarget(p as unknown as Product)}
          />
        </div>
      )}
      {productsAddOpen && (
        <AddProductModal
          brandId={displayBrand.id}
          onClose={() => setProductsAddOpen(false)}
          onSuccess={() => { setProductsAddOpen(false); setProductsRefresh((n) => n + 1); }}
        />
      )}
      {productsEditTarget && (
        <AddProductModal
          brandId={displayBrand.id}
          product={productsEditTarget}
          onClose={() => setProductsEditTarget(null)}
          onSuccess={() => { setProductsEditTarget(null); setProductsRefresh((n) => n + 1); }}
        />
      )}
      {productsDeleteTarget && (
        <DeleteConfirmModal
          title="Delete product"
          message={`Are you sure you want to delete "${productsDeleteTarget.name}"? This cannot be undone.`}
          onClose={() => setProductsDeleteTarget(null)}
          onConfirm={() => deleteProductFromTabMutation.mutate(productsDeleteTarget.id)}
          isLoading={deleteProductFromTabMutation.isPending}
        />
      )}

      {showEditModal && (
        <EditBrandModal brand={displayBrand} onClose={() => setShowEditModal(false)} onSuccess={() => queryClient.invalidateQueries({ queryKey: ['/api/brands'] })} />
      )}
      {showDeleteModal && (
        <DeleteConfirmModal
          title="Delete brand"
          message={`Are you sure you want to delete "${displayBrand.name}" and all its products? This cannot be undone.`}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={() => deleteBrandMutation.mutate()}
          isLoading={deleteBrandMutation.isPending}
        />
      )}
    </div>
  );
}

export function CanvasBrandsView() {
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [showAnalyzeModal, setShowAnalyzeModal] = useState(false);
  const [analyzeModalUrl, setAnalyzeModalUrl] = useState<string | undefined>(undefined);
  const queryClient = useQueryClient();

  // Pop the analyzer modal automatically when the chat handed off a URL.
  // Consume + clear the store field on the way through so we don't
  // re-open the modal if the user navigates away and back.
  const pendingUrl = useCanvasStore((s) => s.pendingBrandModalUrl);
  useEffect(() => {
    if (!pendingUrl) return;
    setAnalyzeModalUrl(pendingUrl);
    setShowAnalyzeModal(true);
    useCanvasStore.setState({ pendingBrandModalUrl: null });
  }, [pendingUrl]);

  const { data: brands = [], isLoading } = useQuery<Brand[]>({
    queryKey: ['/api/brands'],
  });

  // v7.08 — which brands are mid-scan, for the greyed "Analyzing Brand" tiles.
  // ONE bulk call for the whole grid (never one poll per tile), and it only
  // polls while something is actually in flight. Fail-open: the endpoint
  // returns {} on error, so a hiccup just renders normal tiles.
  const { data: scanning = {} } = useQuery<Record<string, string>>({
    queryKey: ['/api/brands/onboarding/active'],
    refetchInterval: (q) => (Object.keys((q.state.data as Record<string, string>) ?? {}).length > 0 ? 3000 : 8000),
  });

  const handleBrandSelected = () => {
    queryClient.invalidateQueries({ queryKey: ['/api/brands'] });
  };

  if (selectedBrand) {
    const initialBrand = brands.find((b) => b.id === selectedBrand.id) || selectedBrand;
    return (
      <div style={{ padding: '24px 32px', minHeight: '100%' }}>
        <BrandDetailView brandId={selectedBrand.id} initialBrand={initialBrand} onBack={() => setSelectedBrand(null)} />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 32px', minHeight: '100%' }}>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Tag style={{ width: 20, height: 20, color: '#8C8578' }} />
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1A1A1A', margin: 0 }}>Brands</h1>
          <span style={{ fontSize: 12, color: '#8C8578', fontWeight: 400 }}>
            {brands.length} brand{brands.length !== 1 ? 's' : ''}
          </span>
        </div>
        <button
          data-testid="button-canvas-analyze-new-brand"
          onClick={() => setShowAnalyzeModal(true)}
          style={{
            padding: '9px 18px',
            borderRadius: 12,
            background: '#1A1A1A',
            color: 'white',
            fontSize: 13.5,
            fontWeight: 600,
            cursor: 'pointer',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 7,
          }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#2D2D2D')}
          onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#1A1A1A')}
        >
          <Plus style={{ width: 15, height: 15 }} />
          Analyze new brand
        </button>
      </div>

      {isLoading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
          {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
          <AddBrandCard onClick={() => setShowAnalyzeModal(true)} />
          {brands.map((brand) => (
            <BrandCard
              key={brand.id}
              brand={brand}
              scanning={Boolean(scanning[String(brand.id)])}
              onClick={() => setSelectedBrand(brand)}
            />
          ))}
        </div>
      )}

      {showAnalyzeModal && (
        <AnalyzeBrandModal
          onClose={() => { setShowAnalyzeModal(false); setAnalyzeModalUrl(undefined); }}
          onSuccess={handleBrandSelected}
          initialUrl={analyzeModalUrl}
        />
      )}
    </div>
  );
}
